import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const YardsToMetersConverter = () => {
  const [inputData, setInputData] = useState({
    yards: '',
    meters: '',
    result: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const convertToMeters = () => {
    const { yards } = inputData;

    // Validate input
    if (!yards) {
      setError('Please enter a value in yards');
      return;
    }

    // Check if input is a valid number
    if (!/^[0-9.]+$/.test(yards)) {
      setError('Please enter a valid number');
      return;
    }

    // Convert yards to meters
    const yardsValue = parseFloat(yards);
    const metersValue = (yardsValue * 0.9144).toFixed(4);
    const result = `${yardsValue} yards = ${metersValue} meters`;

    // Create visualization data
    const chartData = {
      labels: ['Yards', 'Meters'],
      datasets: [
        {
          label: 'Distance',
          data: [yardsValue, parseFloat(metersValue)],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(153, 102, 255, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(153, 102, 255, 1)'],
          borderWidth: 1
        }
      ]
    };

    const conversionSteps = [
      'Step 1: Input the distance in yards',
      `Input Value: ${yardsValue} yards`,
      '\nStep 2: Multiply by the conversion factor (1 yard = 0.9144 meters)',
      `Calculation: ${yardsValue} × 0.9144 = ${metersValue}`,
      '\nStep 3: Display the result in meters',
      `Result: ${metersValue} meters`
    ];

    setResults({
      yardsValue,
      metersValue,
      result,
      conversionSteps,
      chartData
    });
  };

  const exampleData = {
    yards: '10',
    meters: '9.144',
    result: '10 yards = 9.144 meters'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-green-100 to-blue-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Yards to Meters Converter | Distance Conversion Tool</title>
          <meta name="description" content="Convert yards to meters with this simple calculator. This tool helps you quickly convert distances from yards to meters using the standard conversion factor." />
          <meta name="keywords" content="yards to meters, distance converter, unit conversion, yards calculator, meters calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-green-600 mr-4">📏</div>
            <h1 className="text-3xl font-bold text-gray-800">Yards to Meters Converter</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Convert distances from yards to meters using this simple calculator. This tool uses the standard conversion factor (1 yard = 0.9144 meters) to provide accurate results.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Distance in Yards:</label>
              <input
                type="text"
                name="yards"
                value={inputData.yards}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter distance in yards (e.g., 10)"
              />
            </div>
          </div>

          <button
            onClick={convertToMeters}
            className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Convert to Meters
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Conversion Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Conversion Details</h3>
                  <p className="text-lg">Input (Yards): {results.yardsValue}</p>
                  <p className="text-lg">Result (Meters): {results.metersValue}</p>
                  <p className="text-lg">Full Result: {results.result}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Conversion Factor</h3>
                  <p className="text-lg">1 yard = 0.9144 meters</p>
                  <p className="text-lg">Conversion Formula:</p>
                  <p className="text-lg">meters = yards × 0.9144</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Distance'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Unit'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Yards vs Meters Comparison'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Conversion Steps</h3>
                <div className="space-y-4">
                  {results.conversionSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for yards to meters conversion:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Input Yards: {exampleData.yards}</li>
                <li>Converted Meters: {exampleData.meters}</li>
                <li>Result: {exampleData.result}</li>
              </ul>

              <h4 className="mb-4">Conversion Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Multiply the input yards by 0.9144
                  <ul className="list-disc pl-6">
                    <li>10 yards × 0.9144 = 9.144 meters</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Yards to Meters Converter:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Distance in Yards:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the distance you want to convert</li>
                  <li>Decimal values are supported</li>
                </ul>
              </li>
              <li><strong>Click Convert:</strong> The converter will calculate:
                <ul className="list-disc pl-6 mt-2">
                  <li>The equivalent distance in meters</li>
                  <li>A visual comparison of yards and meters</li>
                </ul>
              </li>
              <li><strong>Review Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Check the converted meters value</li>
                  <li>View the conversion steps and formula</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-green-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Use this converter for sports, construction, and travel planning</li>
                <li>Works for both whole numbers and decimals</li>
                <li>Always verify results for critical applications</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Yards and Meters <span className="text-blue-600">🌎</span></h2>
          <div className="text-gray-600">
            <p>
              Yards and meters are units of distance used worldwide. Here's a quick overview:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Yards:</strong> A unit of length in the imperial system, commonly used in the United States, United Kingdom, and other countries</li>
              <li><strong>Meters:</strong> The standard unit of length in the metric system, used globally</li>
              <li><strong>Conversion Factor:</strong> 1 yard = 0.9144 meters</li>
              <li><strong>Common Uses:</strong> Sports fields, construction, and everyday measurements</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Accuracy:</strong> Ensure your input is correct for precise results</li>
              <li><strong>Context:</strong> Understand whether yards or meters are appropriate for your use case</li>
              <li><strong>Double-Check:</strong> Verify results if using for critical applications</li>
              <li><strong>Units:</strong> Always specify the unit when communicating distances</li>
            </ul>
            <p className="italic mt-4">
              Note: This converter uses the standard conversion factor, but slight variations may exist in specific contexts.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default YardsToMetersConverter;